import { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_URL || 'http://localhost:18080');

const Login = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const [footerConfig, setFooterConfig] = useState({});

  // 获取底部备案信息
  useEffect(() => {
    const fetchFooterInfo = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/homepage-config`);
        const data = await res.json();
        if (data.code === 0) {
          setFooterConfig(data.data || {});
        }
      } catch (e) {
        // 忽略错误
      }
    };
    fetchFooterInfo();
  }, []);

  // 生成验证码
  const generateCaptcha = () => {
    const canvas = captchaRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // 清空画布
    ctx.clearRect(0, 0, width, height);
    
    // 生成背景
    ctx.fillStyle = '#f0f2f5';
    ctx.fillRect(0, 0, width, height);
    
    // 生成随机验证码
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let captcha = '';
    for (let i = 0; i < 4; i++) {
      captcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    // 保存验证码到 localStorage
    sessionStorage.setItem('captcha', captcha.toLowerCase());
    
    // 绘制验证码
    ctx.font = 'bold 24px Arial';
    ctx.textBaseline = 'middle';
    const startX = 25;
    const spacing = 25;

    for (let i = 0; i < captcha.length; i++) {
      ctx.save();
      // 随机位置
      const x = startX + i * spacing;
      const y = height / 2;

      // 随机旋转
      const angle = (Math.random() - 0.5) * 0.4;
      ctx.translate(x, y);
      ctx.rotate(angle);
      
      // 随机颜色
      const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'];
      ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
      
      ctx.fillText(captcha[i], 0, 0);
      ctx.restore();
    }
    
    // 添加干扰线
    for (let i = 0; i < 6; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.strokeStyle = `rgba(0, 0, 0, ${Math.random() * 0.3})`;
      ctx.stroke();
    }
    
    // 添加干扰点
    for (let i = 0; i < 50; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, 1, 0, 2 * Math.PI);
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.random() * 0.3})`;
      ctx.fill();
    }
  };

  // 刷新验证码
  const refreshCaptcha = () => {
    generateCaptcha();
  };

  // 组件加载时生成验证码
  useEffect(() => {
    generateCaptcha();
  }, []);

  const onFinish = async (values) => {
    console.log('=== onFinish 被调用 ===');
    console.log('表单提交 - 完整 values:', values);

    // 从 DOM 直接获取验证码输入值
    const captchaInput = document.getElementById('captcha-input');
    const captchaValue = captchaInput ? captchaInput.value : '';
    console.log('DOM 获取的验证码:', captchaValue);

    // 验证验证码
    const storedCaptcha = sessionStorage.getItem('captcha');
    console.log('存储的验证码:', storedCaptcha);

    if (!captchaValue || captchaValue.trim() === '') {
      message.error('请输入验证码');
      return;
    }

    const inputLower = captchaValue.toLowerCase().trim();
    console.log('输入转小写:', inputLower);
    console.log('对比结果:', inputLower, '===', storedCaptcha, '?', inputLower === storedCaptcha);

    if (inputLower !== storedCaptcha) {
      message.error('验证码错误');
      refreshCaptcha();
      return;
    }

    console.log('=== 验证码验证通过，开始登录流程 ===');
    setLoading(true);
    try {
      console.log('正在调用登录 API...');
      const response = await authAPI.login(values.username, values.password);
      console.log('登录 API 响应:', response);

      if (response.code === 0 && response.data && response.data.token) {
        const token = response.data.token;
        localStorage.setItem('admin_token', token);
        message.success('登录成功');
        console.log('Token 已保存到 localStorage:', token);
        console.log('准备跳转到首页...');
        console.log('当前 URL:', window.location.href);

        // 使用 window.location.href 强制跳转
        console.log('执行 window.location.href = "/"');
        window.location.href = '/';
      } else {
        message.error(response.message || '登录失败');
        refreshCaptcha();
      }
    } catch (error) {
      console.error('登录错误:', error);
      message.error('登录失败，请检查账号和密码');
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">灵狐算力</h1>
        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入账号' },
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="管理员账号" 
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '24px' }}>
            <div style={{ flex: 1 }}>
              <Input
                prefix={<SafetyOutlined />}
                placeholder="验证码"
                maxLength={4}
                id="captcha-input"
                name="captcha"
                onChange={(e) => {
                  console.log('captcha 输入变化:', e.target.value);
                }}
              />
            </div>
            <canvas
              ref={captchaRef}
              width={120}
              height={40}
              style={{ cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: '6px' }}
              onClick={refreshCaptcha}
              title="点击刷新验证码"
            />
          </div>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              登录
            </Button>
          </Form.Item>
        </Form>
      </div>
      <div className="login-footer" style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, lineHeight: '22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 12 }}>
          {footerConfig.footer_copyright && (
            <span style={{ marginRight: 8 }}>{footerConfig.footer_copyright}</span>
          )}
          {footerConfig.footer_police && footerConfig.footer_police_code && (
            <a href={`https://beian.mps.gov.cn/#/query/webSearch?code=${footerConfig.footer_police_code}`}
               target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none' }}>
              {footerConfig.footer_police || '京公网安备'}{footerConfig.footer_police_code}
            </a>
          )}
          {footerConfig.footer_icp && (
            <a href="https://beian.miit.gov.cn/#/Integrated/index" target="_blank" rel="noreferrer"
               style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none' }}>
              {footerConfig.footer_icp}
            </a>
          )}
          {footerConfig.footer_telecom && (
            <a href={`https://tsm.miit.gov.cn/dxxzsp/xkz/xkzgl/resource/qiyesearch.jsp?num=${encodeURIComponent(footerConfig.footer_telecom)}&type=xuke`}
               target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none' }}>
              增值电信业务经营许可证: {footerConfig.footer_telecom}
            </a>
          )}
        </div>
        {footerConfig.footer_biz_id && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{footerConfig.footer_biz_id}</p>
        )}
      </div>
    </div>
  );
};

export default Login;
